import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Alert, Button, App } from "antd";
import DataTable from "../../../components/DataTable";
import DuplicateModal, {
  DuplicateFormValues,
} from "../../../components/DuplicateModal";
import ActivityIndicator from "../../../components/ActivityIndicator";
import { ProgramEntity } from "../../../services/programs/programs.types";
import { apiSlice } from "../../../store/api/apiSlice";
import {
  useGetProgramsQuery,
  useDeleteProgramMutation,
  useDuplicateProgramMutation,
} from "../../../store/api/programsApi";
import TableSearchBar from "../../../components/TableSearchBar";
import useDebounce from "../../../hooks/useDebounce";
import { showDeleteUndoNotification } from "../../../utils/delete-undo";
import PageHeader from "../../../components/PageHeader";
import { useIsInitialLoad } from "../../../hooks/useIsInitialLoad";

const DEFAULT_PAGE_SIZE = 10;

const Programs: React.FC = () => {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // The URL is the source of truth for the filters and the (client-side)
  // pagination page: the list is deep-linkable and survives refreshes.
  // Inputs keep their own local state and sync into the URL after the
  // debounce below.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = parseInt(searchParams.get("page") ?? "", 10) || 1;
  const pageSize =
    parseInt(searchParams.get("pageSize") ?? "", 10) || DEFAULT_PAGE_SIZE;
  const idParam = searchParams.get("id") ?? "";
  const titleParam = searchParams.get("title") ?? "";
  const codeParam = searchParams.get("code") ?? "";

  const [searchId, setSearchId] = useState(idParam);
  const [searchTitle, setSearchTitle] = useState(titleParam);
  const [searchCode, setSearchCode] = useState(codeParam);

  const debouncedSearchId = useDebounce(searchId, 500);
  const debouncedSearchTitle = useDebounce(searchTitle, 500);
  const debouncedSearchCode = useDebounce(searchCode, 500);

  // Debounced inputs → URL. Changing a filter always resets to page 1.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const currentId = prev.get("id") ?? "";
        const currentTitle = prev.get("title") ?? "";
        const currentCode = prev.get("code") ?? "";
        if (
          currentId === debouncedSearchId &&
          currentTitle === debouncedSearchTitle &&
          currentCode === debouncedSearchCode
        ) {
          return prev;
        }
        const next = new URLSearchParams(prev);
        if (debouncedSearchId) next.set("id", debouncedSearchId);
        else next.delete("id");
        if (debouncedSearchTitle) next.set("title", debouncedSearchTitle);
        else next.delete("title");
        if (debouncedSearchCode) next.set("code", debouncedSearchCode);
        else next.delete("code");
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [
    debouncedSearchId,
    debouncedSearchTitle,
    debouncedSearchCode,
    setSearchParams,
  ]);

  // The backend has no search params for programs, so the query loads the
  // full list and the filtering below stays client-side. `data` (instead of
  // `currentData`) keeps the previous list visible while refetching.
  const { data, isFetching, isError, refetch } = useGetProgramsQuery();
  const [deleteProgram] = useDeleteProgramMutation();
  const [duplicateProgram, { isLoading: isDuplicating }] =
    useDuplicateProgramMutation();
  const [programToDuplicate, setProgramToDuplicate] =
    useState<ProgramEntity | null>(null);

  const programs = data ?? [];

  // program_code can be null (e.g. duplicated programs without a new code),
  // so the code filter must not assume a string.
  const filteredPrograms = programs.filter(
    (program) =>
      program.title.toLowerCase().includes(titleParam.toLowerCase()) &&
      program.id.toString().includes(idParam) &&
      (program.program_code ?? "")
        .toLowerCase()
        .includes(codeParam.toLowerCase()),
  );

  const isInitialLoad = useIsInitialLoad(isFetching, programs);

  const handlePageChange = (newPage: number, newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      next.set("pageSize", String(newPageSize));
      return next;
    });
  };

  const handleEdit = (record: ProgramEntity) => {
    navigate(`/programs/${record.id}`, { state: record.id });
  };

  const handleDelete = async (record: ProgramEntity) => {
    try {
      await deleteProgram(record.id).unwrap();
      // La notificación de éxito es la propia notificación de "Deshacer"; si
      // la restauración ocurre, invalidar el tag refresca la lista cacheada.
      showDeleteUndoNotification({
        entity: "program",
        id: record.id,
        title: record.title,
        onRestored: () => {
          dispatch(
            apiSlice.util.invalidateTags([
              { type: "Program", id: "LIST" },
              "Trash",
            ]),
          );
        },
      });
    } catch (error: any) {
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.data?.message || "El programa no pudo ser eliminado!",
        placement: "top",
        duration: 3,
      });
    }
  };

  const handleDuplicateConfirm = async ({
    title,
    children_mode,
    program_code,
  }: DuplicateFormValues) => {
    // program_code is required by the modal's validation for programs.
    if (!programToDuplicate || !program_code) return;
    try {
      const copy = await duplicateProgram({
        id: programToDuplicate.id,
        title,
        children_mode,
        program_code,
      }).unwrap();
      setProgramToDuplicate(null);
      notification["success"]({
        message: "Programa duplicado",
        description:
          children_mode === "link"
            ? `Se creó "${copy.title}" asociando los mismos cursos, proyectos y recursos del original.`
            : `Se creó "${copy.title}" con copias independientes de sus cursos, lecciones y proyectos.`,
        placement: "top",
        duration: 5,
        btn: (
          <Button
            type="primary"
            onClick={() => navigate(`/programs/${copy.id}`)}
          >
            Editar copia
          </Button>
        ),
      });
    } catch (error: any) {
      notification["error"]({
        message: "Duplicación errónea",
        description:
          error?.data?.message || "El programa no pudo ser duplicado.",
        placement: "top",
        duration: 3,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Programas"
        extra={
          <TableSearchBar
            fields={[
              {
                placeholder: "Buscar por ID",
                value: searchId,
                onChange: setSearchId,
                width: 200,
              },
              {
                placeholder: "Buscar por título",
                value: searchTitle,
                onChange: setSearchTitle,
                width: 200,
              },
              {
                placeholder: "Buscar por código",
                value: searchCode,
                onChange: setSearchCode,
                width: 200,
              },
            ]}
          >
            <Button type="primary" onClick={() => navigate("/programs/new")}>
              Nuevo programa
            </Button>
          </TableSearchBar>
        }
      />
      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudieron cargar los programas."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
        />
      )}
      {isInitialLoad ? (
        <ActivityIndicator />
      ) : (
        <DataTable
          data={filteredPrograms}
          columns={[
            { title: "ID", dataIndex: "id", key: "id" },
            { title: "Título", dataIndex: "title", key: "title" },
            {
              title: "Código",
              dataIndex: "program_code",
              key: "program_code",
            },
            {
              title: "Grado",
              dataIndex: ["grade", "grade"],
              key: "grade",
              render: (_: any, record: ProgramEntity) =>
                record.grade?.grade ?? "",
            },
          ]}
          onEdit={handleEdit}
          onDuplicate={(record) => setProgramToDuplicate(record)}
          onDelete={handleDelete}
          loading={isFetching}
          pagination={{
            current: page,
            pageSize,
            total: filteredPrograms.length,
            onChange: handlePageChange,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
        />
      )}
      <DuplicateModal
        open={programToDuplicate !== null}
        entityLabel="programa"
        originalTitle={programToDuplicate?.title ?? ""}
        childrenLabel="los cursos, proyectos y recursos del programa"
        showProgramCode
        loading={isDuplicating}
        onCancel={() => setProgramToDuplicate(null)}
        onConfirm={handleDuplicateConfirm}
      />
    </div>
  );
};

export default Programs;
